import React from 'react';
import { CloseButton } from '@UI-Elements/Buttons/CloseButton';
import { Button, CircularProgress, Input, MenuItem, Select, Chip, Box } from '@mui/material';
import { ToggleChips } from '../../Dashboard/dashboard-components/ToggleChips';
import { Account } from '../../../sp-types/accountTypes';
import { Material } from '../../../sp-types/materialType';
import { updateAccount } from '../../../sp-firebase/accountFB';

interface IOpts {
  isSalesCommitment: boolean;
  titleOverrides: Record<string, string>;
  addMaterials: string[];
  isDirty: boolean;
}

interface Props {
  close: () => void;
  accounts: Account[];
  materials: Material[];
  materialsMap: Record<string, Material>;
}

export const AccountOptsPopup: React.FC<Props> = ({ close, accounts, materials, materialsMap }) => {
  const [selectedAccountId, setSelectedAccountId] = React.useState<string>(accounts[0]?.id || '');
  const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);

  const [accountOps, setAccountOpts] = React.useState<IOpts>({
    isSalesCommitment: false,
    titleOverrides: {},
    addMaterials: [],
    isDirty: false,
  });

  React.useEffect(() => {
    if (selectedAccount) {
      setAccountOpts({
        isSalesCommitment: !!selectedAccount.isSalesCommitment,
        titleOverrides: selectedAccount.titleOverrides || {},
        addMaterials: selectedAccount.addMaterials || [],
        isDirty: false,
      });
    }
  }, [selectedAccount]);

  const [loading, setLoading] = React.useState<boolean>(false);

  const save = async () => {
    if (selectedAccount) {
      setLoading(true);
      const { isDirty, ...accountData } = accountOps;
      await updateAccount(selectedAccount.id || '', { ...selectedAccount, ...accountData });
      setLoading(false);
      close();
    }
  };

  const setVal = (key: string, val: string | boolean | string[]) => {
    setAccountOpts((prev) => ({ ...prev, [key]: val, isDirty: true }));
  };

  const setOverride = (titleKey: string, val: string) => {
    setAccountOpts((prev) => ({
      ...prev,
      titleOverrides: { ...prev.titleOverrides, [titleKey]: val },
      isDirty: true,
    }));
  };

  const removeMaterial = (formulaIdToRemove: string) => {
    setVal(
      'addMaterials',
      accountOps.addMaterials.filter((id) => id !== formulaIdToRemove),
    );
  };

  // Get available materials (exclude materials already in account)
  const availableMaterials = React.useCallback(() => {
    const formulaIdsMap =
      selectedAccount?.formulasToAccount?.reduce(
        (acc, { formulaId }) => ({ ...acc, [formulaId]: true }),
        {} as Record<string, boolean>,
      ) || {};
    return materials.filter(({ id }) => !formulaIdsMap[id]);
  }, [selectedAccount, materials]);

  const titleOverrideKeys = ['futureOrders', 'inventory'];

  return (
    <div className="flex flex-col p-6 gap-6 bg-white rounded shadow-custom relative min-w-[600px]">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Edit Account Options</h2>
        <CloseButton onClick={close} />
      </div>

      {/* Account Selection */}
      <div className="flex flex-col gap-3">
        <label className="font-medium">Account:</label>
        <Select
          value={selectedAccountId}
          onChange={(e) => setSelectedAccountId(e.target.value)}
          className="w-full"
          size="small"
        >
          {accounts.map((account) => (
            <MenuItem key={account.id} value={account.id}>
              <span className="font-medium text-blue-600">{account.id}</span>
            </MenuItem>
          ))}
        </Select>
      </div>

      {/* Title Overrides */}
      <div className="flex flex-col gap-3">
        <label className="font-medium">Title Overrides:</label>
        <div className="grid grid-cols-1 gap-3">
          {titleOverrideKeys.map((titleKey) => (
            <div key={titleKey} className="flex items-center gap-4">
              <label className="w-32 text-right font-medium">{titleKey}:</label>
              <Input
                className="flex-1"
                value={accountOps.titleOverrides[titleKey] || ''}
                onChange={(e) => setOverride(titleKey, e.target.value)}
                placeholder={`Enter ${titleKey} title override`}
                size="small"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Sales Commitment Toggle */}
      <div className="flex flex-col gap-3">
        <label className="font-medium">Sales commitment / Future orders:</label>
        <div className="flex justify-center">
          <ToggleChips
            chips={[
              { key: 'Sales commitment', value: 1 },
              { key: 'Future orders', value: 0 },
            ]}
            selected={accountOps.isSalesCommitment ? 1 : 0}
            setSelected={(val: number) => setVal('isSalesCommitment', val === 1)}
            width="8rem"
          />
        </div>
      </div>

      {/* Material Selection */}
      <div className="flex flex-col gap-3">
        <label className="font-medium">Add Materials:</label>
        <Select
          multiple
          value={accountOps.addMaterials}
          onChange={({ target }) =>
            setVal('addMaterials', typeof target.value === 'string' ? [target.value] : target.value)
          }
          className="w-full max-w-[500px]"
          size="small"
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '460px' }}>
              {selected.map((formulaId) => (
                <Chip
                  key={formulaId}
                  label={materialsMap[formulaId]?.name || formulaId}
                  size="small"
                  onDelete={() => removeMaterial(formulaId)}
                  deleteIcon={<span className="font-md cursor-pointer hover:border">×</span>}
                />
              ))}
            </Box>
          )}
        >
          {availableMaterials().map((material) => (
            <MenuItem key={material.id} value={material.id}>
              {material.name} ({material.id})
            </MenuItem>
          ))}
        </Select>
        {availableMaterials().length === 0 && (
          <p className="text-gray-500 text-sm">All materials are already added to this account</p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 justify-center pt-4 border-t">
        <Button variant="outlined" color="primary" className="w-32" onClick={close} disabled={loading}>
          CANCEL
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="w-32"
          onClick={save}
          disabled={!accountOps.isDirty || loading}
        >
          {loading ? <CircularProgress size={20} /> : 'SAVE'}
        </Button>
      </div>
    </div>
  );
};
